import { useEffect } from "react";
import Navigation from "@/components/Navigation";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Cloud,
  Heart,
  Award,
  CheckCircle,
  Users,
  Laptop,
  Server,
  Workflow,
  Package,
  Handshake,
  Repeat,
  Globe,
  Lightbulb,
  ClipboardList,
  Search,
  PenTool,
  Rocket,
  Users2,
  DollarSign,
  ShieldCheck,
  Headphones,
  Wrench,
  Shield,
  BarChart3,
  Clock,
} from "lucide-react";
import { Link } from "react-router-dom";
import heroBackground from "@/assets/hero-background.webp";
import PrimaryCTA from "@/components/PrimaryCTA";
import Footer from "@/components/Footer";

const Index = () => {
  const services = [
    {
      icon: Laptop,
      title: "Modern Workplace",
      description:
        "Enable collaboration, productivity, and security from any location with standardized devices, identity-based access, and streamlined workflows. Our modern workplace solutions integrate email, chat, and document collaboration with compliance policies, empowering teams to work efficiently while IT remains secure and manageable."
    },
    {
      icon: Server,
      title: "Managed IT Services",
      description:
        "Proactive monitoring, patching, and technical support keep your hybrid infrastructure secure, stable, and always available. We align IT services with business goals to reduce downtime and improve productivity. With clear SLAs, automated maintenance, and responsive help desk support, your business can focus on growth while we handle the technical details."
    },
    {
      icon: Cloud,
      title: "Cloud & Infrastructure",
      description:
        "Seamless transition to cloud or hybrid environments with governance, cost optimization, and disaster recovery planning. Our consultation ensures resilience and predictable expenses. From migration planning to backup strategies, we design scalable architectures that deliver performance, security, and transparency for your business operations."
    },
    {
      icon: Workflow,
      title: "Digital Transformation Strategy",
      description:
        "Roadmaps, assessments, and change management designed to align technology with business outcomes. We help organizations automate processes, improve security, and adopt new solutions with confidence. Our digital transformation consultancy focuses on measurable improvements by reducing manual steps, increasing efficiency, and enabling long-term innovation."
    },
  ];

  // Unified timeline: process + operations support
  const allSteps = [
    { title: "Initial Consultation", description: "We take time to understand your specific requirements and obstacles, laying the groundwork for the right strategy.", icon: Lightbulb },
    { title: "Proposal Development", description: "We prepare a customized roadmap designed around your project’s objectives.", icon: ClipboardList },
    { title: "System Audit", description: "We analyze your current environment to highlight strengths and uncover areas that can be improved.", icon: Search },
    { title: "Solution Design", description: "We create a tailored architecture that supports your immediate goals and scales with future needs.", icon: PenTool },
    { title: "Proof of Concept", description: "We validate the solution in a controlled environment before rollout.", icon: ShieldCheck },
    { title: "Implementation", description: "We deploy the solution in practice, making sure it integrates seamlessly with your existing infrastructure.", icon: Rocket },
    { title: "Cost Optimization", description: "We adjust and refine the setup to maximize value, reducing expenses while maintaining high performance.", icon: DollarSign },
    { title: "Operations Support", description: "We provide ongoing technical assistance to keep your systems stable and efficient across all platforms.", icon: Headphones },
    { title: "Team Enablement", description: "We strengthen your workforce through hands on training and, where needed, by supporting new talent acquisition.", icon: Users2 },
       
  ];

  // Reveal on scroll (Tailwind transitions)
  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add("opacity-100", "translate-x-0");
            entry.target.classList.remove("opacity-0", "-translate-x-10", "translate-x-10");
          }
        });
      },
      { threshold: 0.2 }
    );
    document.querySelectorAll(".reveal").forEach(el => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <Navigation />

      {/* Hero */}
      <section
        className="relative w-full px-4 py-20 md:py-32 overflow-hidden"
        style={{
          backgroundImage: `linear-gradient(rgba(0,0,0,0.5), rgba(0,0,0,0.5)), url(${heroBackground})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <h1 className="text-4xl md:text-6xl font-bold mb-6 text-white">
            Managed IT Services, Cloud & Modern Workplace Solutions
          </h1>
          <p className="text-xl md:text-2xl text-white/90 mb-8">
            TechG8 delivers IT services, consultation, and digital transformation strategies that empower businesses to scale securely and efficiently.
          </p>
          <PrimaryCTA />
        </div>
      </section>

      {/* Services */}
      <section className="bg-muted py-16">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Key Services</h2>
            <p className="text-lg text-muted-foreground">Enterprise grade solutions tailored for your business</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.map((s, i) => {
              const Icon = s.icon;
              return (
                <Card key={i} className="hover:shadow-lg transition-shadow">
                  <CardHeader>
                    <Icon className="h-10 w-10 text-primary mb-3" />
                    <CardTitle className="text-lg">{s.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CardDescription>{s.description}</CardDescription>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Unified Timeline (consistent spine, dots touch cards, starts at first dot) */}
      <section className="bg-white py-16 relative">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Our Process to Scale Your Operations</h2>
            <p className="text-lg text-muted-foreground">A structured approach to ensure smooth delivery and long term success.</p>
          </div>

          <div className="relative">
            {/* Continuous spine from the first row to the last */}
            <div className="absolute left-1/2 top-0 bottom-0 w-[2px] bg-primary/30 -translate-x-1/2" />

            <div className="space-y-12">
              {allSteps.map((step, idx) => {
                const Icon = step.icon;
                const isLeft = idx % 2 === 0;
                const isFirst = idx === 0;
                const isLast = idx === allSteps.length - 1;

                return (
                  <div key={idx} className="grid grid-cols-[1fr_auto_1fr] items-center gap-4 relative">
                    {/* Left card */}
                    {isLeft ? (
                      <Card className="w-[20rem] justify-self-end shadow-md reveal opacity-0 -translate-x-10 transition-all duration-1000 ease-out">
                        <CardHeader className="pb-2">
                          <div className="flex items-center">
                            <div className="w-9 h-9 rounded-full bg-primary text-white flex items-center justify-center">
                              <Icon className="h-5 w-5" />
                            </div>
                            <CardTitle className="ml-3 text-base">{step.title}</CardTitle>
                          </div>
                        </CardHeader>
                        <CardContent className="pt-0">
                          <CardDescription className="text-left text-sm">{step.description}</CardDescription>
                        </CardContent>
                      </Card>
                    ) : (
                      <div />
                    )}

                    {/* Center column: dot + per-row spine segments to guarantee start/end */}
                    <div className="flex flex-col items-center relative">
                      {/* Hidden above segment for the very first dot so the spine visually starts at it */}
                      {!isFirst && <div className="w-[2px] flex-1 bg-transparent" />}
                      <div className="w-4 h-4 rounded-full bg-primary border-4 border-white shadow z-10" />
                      {/* Hidden below segment for the very last dot */}
                      {!isLast && <div className="w-[2px] flex-1 bg-transparent" />}
                    </div>

                    {/* Right card */}
                    {!isLeft ? (
                      <Card className="w-[20rem] justify-self-start shadow-md reveal opacity-0 translate-x-10 transition-all duration-1000 ease-out">
                        <CardHeader className="pb-2">
                          <div className="flex items-center">
                            <div className="w-9 h-9 rounded-full bg-primary text-white flex items-center justify-center">
                              <Icon className="h-5 w-5" />
                            </div>
                            <CardTitle className="ml-3 text-base">{step.title}</CardTitle>
                          </div>
                        </CardHeader>
                        <CardContent className="pt-0">
                          <CardDescription className="text-left text-sm">{step.description}</CardDescription>
                        </CardContent>
                      </Card>
                    ) : (
                      <div />
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Social Proof */}
      <section className="container mx-auto px-4 py-16">
        <div className="max-w-4xl mx-auto text-center">
          <div className="flex items-center justify-center gap-2 mb-4">
            <Award className="h-8 w-8 text-primary" />
            <h2 className="text-3xl font-bold">Why Work With Us</h2>
          </div>

          <div className="text-left mt-8 space-y-4">
            <div className="flex items-start gap-3">
              <Heart className="h-6 w-6 text-primary flex-shrink-0 mt-0.5" />
              <p className="text-lg text-muted-foreground">
                <strong>We Care About Your Success:</strong> Beyond technology, we prioritize people, trust, and long‑term relationships.
              </p>
            </div>
            <div className="flex items-start gap-3">
              <Users className="h-6 w-6 text-primary flex-shrink-0 mt-0.5" />
              <p className="text-lg text-muted-foreground">
                <strong>Expert Team:</strong> Seasoned professionals with deep technical expertise and industry insight.
              </p>
            </div>
            <div className="flex items-start gap-3">
              <CheckCircle className="h-6 w-6 text-primary flex-shrink-0 mt-0.5" />
              <p className="text-lg text-muted-foreground">
                <strong>Proven Experience:</strong> Successful projects across government, finance, education, insurance, and retail.
              </p>
            </div>
            <div className="flex items-start gap-3">
              <Package className="h-6 w-6 text-primary flex-shrink-0 mt-0.5" />
              <p className="text-lg text-muted-foreground">
                <strong>End‑to‑End Delivery:</strong> Comprehensive services from ideation through ongoing support.
              </p>
            </div>
            <div className="flex items-start gap-3">
              <Handshake className="h-6 w-6 text-primary flex-shrink-0 mt-0.5" />
              <p className="text-lg text-muted-foreground">
                <strong>Personal Commitment:</strong> Direct collaboration with an expert who understands both technical and business needs.
              </p>
            </div>
            <div className="flex items-start gap-3">
              <Repeat className="h-6 w-6 text-primary flex-shrink-0 mt-0.5" />
              <p className="text-lg text-muted-foreground">
                <strong>Agile Methodology:</strong> Flexible, iterative processes that adapt to evolving requirements.
              </p>
            </div>
            <div className="flex items-start gap-3">
              <Globe className="h-6 w-6 text-primary flex-shrink-0 mt-0.5" />
              <p className="text-lg text-muted-foreground">
                <strong>Client‑Centric Approach:</strong> Dedicated project management and clear communication at every stage.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <div className="text-center py-12 bg-muted rounded-lg">
        <h2 className="text-3xl font-bold mb-4">Book a Free IT Consultation Today</h2>
          <p className="text-muted-foreground mb-6">
            Ready to transform your business with managed IT services, cloud migration, or modern workplace solutions? 
            At TechG8, we design secure, scalable, and cost-effective strategies tailored to your goals. 
            Let’s discuss how our consultation can help you modernize operations, strengthen security, and empower your workforce.
          </p>
        <PrimaryCTA />
      </div>
      
      <Footer />
    </div>
  );
};

export default Index;
